import { Lightning, Router } from "@lightningjs/sdk";
import { Card, CentredClipsRow, FOCUS_TRANSITION, GlassScene, GlassSurface, TeamRoster, WatchButton, WatchScore, WATCH_GLASS, WATCH_BLUE, WATCH_PINK, WATCH_PANEL_WIDTH, watchAsset, watchText, } from "core.components";
import { demoVideos } from "./demoVideos";
const TAB_TRANSITION = { duration: 0.2, timingFunction: "ease-out" };
const CLIPS = [
    [
        "highlight-aggregate-victory.jpg",
        "Hosts squeeze to 5-4 aggregate victory as Coulibaly sent off",
    ],
    [
        "highlight-monaco-lens.jpg",
        "Monaco go top of Ligue 1 as woes continue for Lens",
    ],
    ["highlight-dembele.jpg", "Last-gasp Dembele strike earns PSG"],
    [
        "highlight-psg-season.jpg",
        "Champions Paris St-Germain are still searching for their first Ligue 1 win of the season",
    ],
    [
        "highlight-monaco-psg.jpg",
        "AS Monaco lose the first leg with 10 men against PSG",
    ],
];
export class WatchDetails extends Lightning.Component {
    constructor() {
        super(...arguments);
        this._tabIndex = 0;
        this._contentFocused = false;
        this._href = "";
    }
    static _template() {
        return {
            w: 1920,
            h: 1080,
            clipping: true,
            Scene: {
                type: (GlassScene),
                w: 1920,
                h: 1080,
                Backdrop: {
                    w: 1920,
                    h: 1080,
                    src: watchAsset("background.jpg"),
                },
                Score: { type: WatchScore, x: 287, y: 547 },
                Roster: { type: TeamRoster, x: 488, y: 208, alpha: 0 },
                Clips: {
                    x: 287,
                    y: 547,
                    w: WATCH_PANEL_WIDTH,
                    h: 343,
                    alpha: 0,
                    Glass: {
                        type: GlassSurface,
                        w: WATCH_PANEL_WIDTH,
                        h: 343,
                        glassOptions: WATCH_GLASS,
                    },
                    Tint: {
                        w: WATCH_PANEL_WIDTH,
                        h: 343,
                        rect: true,
                        color: 0x330d001e,
                        shader: { type: Lightning.shaders.RoundedRectangle, radius: 24 },
                    },
                    Row: { type: CentredClipsRow, y: 49 },
                    Heading: {
                        x: 485,
                        y: -25,
                        Background: {
                            w: 378,
                            h: 50,
                            rect: true,
                            shader: {
                                type: Lightning.shaders.RoundedRectangle,
                                radius: 14,
                                fillColor: WATCH_BLUE,
                                stroke: 1,
                                strokeColor: 0xffffffff,
                            },
                        },
                        Label: {
                            text: Object.assign(Object.assign({}, watchText("HIGHLIGHTS", 34, 378)), { h: 50, textColor: WATCH_PINK }),
                        },
                    },
                },
            },
            Actions: {
                x: 246.5,
                y: 918,
                w: 1428,
                h: 70,
                Background: {
                    w: 1428,
                    h: 70,
                    rect: true,
                    shader: {
                        type: Lightning.shaders.RoundedRectangle,
                        radius: 8,
                        fillColor: 0x1a000000,
                        stroke: 1,
                        strokeColor: 0x33ffffff,
                    },
                },
                Focus: {
                    w: 357,
                    h: 70,
                    alpha: 0,
                    rect: true,
                    color: Card.BORDER_COLOR,
                    shader: { type: Lightning.shaders.RoundedRectangle, radius: 8 },
                },
                Buttons: {
                    children: [
                        "WATCH LIVE",
                        "TEAM LINEUP",
                        "HIGHLIGHTS",
                        "WATCHLIST",
                    ].map((label, index) => (Object.assign({ type: WatchButton, x: index * 357, label, 
                        // The action rail supplies one sliding focus background.
                        Focus: { visible: false } }, (index === 3 && { icon: "plus" })))),
                },
            },
        };
    }
    set params({ href }) {
        if (href && href !== this._href) {
            this._href = href;
            this.tag("Scene").tag("Clips").tag("Row").items = CLIPS.map(([image, description]) => ({
                description,
                href: demoVideos.lensLyon,
                posterSrc: watchAsset(image),
            }));
        }
    }
    _onMounted() {
        this.reset();
    }
    _onChanged() {
        // Keep the current panel and clip when returning from playback.
        if (Router.getActiveRoute() === "home")
            this.reset();
    }
    reset() {
        this._tabIndex = 0;
        this._contentFocused = false;
        this.widgets.navbar.patch({ y: 0 });
        this.updatePanel();
        this._refocus();
    }
    _getFocused() {
        if (this._contentFocused) {
            const scene = this.tag("Scene");
            return this._tabIndex === 1
                ? scene.tag("Roster")
                : scene.tag("Clips").tag("Row");
        }
        return this.tag("Actions.Buttons").children[this._tabIndex];
    }
    _focus() {
        this.updateActionFocus(false);
    }
    _focusChange() {
        this.updateActionFocus();
    }
    _unfocus() {
        this.tag("Actions.Focus").setSmooth("alpha", 0, FOCUS_TRANSITION);
    }
    updateActionFocus(animate = true) {
        var _a;
        const focus = this.tag("Actions.Focus");
        const x = this._tabIndex * 357;
        if (animate) {
            focus.setSmooth("x", x, TAB_TRANSITION);
        }
        else {
            (_a = focus.transition("x")) === null || _a === void 0 ? void 0 : _a.stop();
            focus.x = x;
        }
        focus.setSmooth("alpha", this._contentFocused ? 0 : 1, FOCUS_TRANSITION);
    }
    _handleLeft() {
        this.selectTab(Math.max(0, this._tabIndex - 1));
        return true;
    }
    _handleRight() {
        this.selectTab(Math.min(3, this._tabIndex + 1));
        return true;
    }
    selectTab(index) {
        this._tabIndex = index;
        this._contentFocused = false;
        this.updatePanel();
        this._refocus();
    }
    updatePanel() {
        const scene = this.tag("Scene");
        scene
            .tag("Score")
            .setSmooth("alpha", this._tabIndex === 0 || this._tabIndex === 3 ? 1 : 0, TAB_TRANSITION);
        scene
            .tag("Roster")
            .setSmooth("alpha", this._tabIndex === 1 ? 1 : 0, TAB_TRANSITION);
        scene
            .tag("Clips")
            .setSmooth("alpha", this._tabIndex === 2 ? 1 : 0, TAB_TRANSITION);
        this.tag("Actions.Buttons").children.forEach((button, index) => {
            button.patch({
                selected: index === this._tabIndex && this._contentFocused,
            });
        });
    }
    enterContent() {
        this._contentFocused = true;
        this.updatePanel();
        this._refocus();
    }
    _handleUp() {
        if (!this._contentFocused && (this._tabIndex === 1 || this._tabIndex === 2))
            this.enterContent();
        else
            Router.focusWidget("Navbar");
        return true;
    }
    _handleDown() {
        if (this._contentFocused)
            this.selectTab(this._tabIndex);
        return true;
    }
    _handleEnter() {
        if (this._tabIndex === 0 && this._href)
            Router.navigate("video", {
                href: this._href,
                title: "PSG vs Marseille",
                category: "Ligue 1",
                mode: "live",
            });
        else if (this._tabIndex === 1 || this._tabIndex === 2)
            this.enterContent();
        // Watchlist is intentionally a no-op until the service is available.
        return true;
    }
    _handleBack() {
        return true;
    }
    _handleBackRelease() {
        if (this._contentFocused)
            this.selectTab(this._tabIndex);
        else if (Router.getHistory().length)
            Router.back();
        else
            Router.navigate("home");
        return true;
    }
}
export default WatchDetails;
//# sourceMappingURL=WatchDetails.js.map